import './ClassbookCourseStats.scss';

import { ArrowSyncRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';

export interface ClassbookCourseStatsProps {
  onBtnShowSeatingPlanClick: () => void;
  onBtnShowAbsencesClick: () => void;
  onBtnShowHomeworkClick: () => void;
  onBtnShowClassServicesClick: () => void;
  isAbsencesLoading: boolean;
  onReloadAbsencesClick: () => void;
  statsStudentsCount: number;
  statsAbsentStudentsCount: number;
  statsOpenHomeworkCount: number;
  statsClassServicesCount: number;
  onStudentRandomizerClick?: () => void;
}

export enum StatsCardTotalValueColor {
  Red = '#c4314b',
  Green = '#2e7d32',
}

export interface StatsCardProps {
  value: number;
  label: string;
  totalValue?: number;
  totalValueColor?: StatsCardTotalValueColor;
  onRightCornerBtnClick?: () => void;
  rightCornerLoading?: boolean;
  rightCornerIcon?: JSX.Element;
  rightCornerAriaLabel?: string;
  btnLabel?: string;
  btnAriaLabel?: string;
  onBtnClick?: () => void;
  secondaryBtnLabel?: string;
  secondaryBtnAriaLabel?: string;
  onSecondaryBtnClick?: () => void;
  comingSoon?: boolean;
  subInfo?: JSX.Element;
}

/**
 * @todo move to own component file
 */
export const StatsCard: React.FC<StatsCardProps> = ({
  value,
  label,
  btnLabel,
  btnAriaLabel,
  onRightCornerBtnClick,
  rightCornerLoading,
  rightCornerIcon,
  rightCornerAriaLabel,
  onBtnClick,
  secondaryBtnLabel,
  secondaryBtnAriaLabel,
  onSecondaryBtnClick,
  totalValue,
  totalValueColor,
  comingSoon,
  subInfo,
}) => {
  const { t } = useTranslation();

  return (
    <div className="statsCardWrapper">
      <div className="statsCard">
        <div className="labelWrapper">
          <div className="label">
            <span className="value">{value}</span>
            {totalValue !== undefined && (
              <span className="value">
                /{' '}
                <span
                  style={{
                    color: totalValueColor ?? 'unset',
                  }}
                >
                  {totalValue}
                </span>
              </span>
            )}
            {label}
          </div>
          {rightCornerIcon && onRightCornerBtnClick && (
            <div
              className={cn(
                rightCornerLoading
                  ? 'rightCornerButtonWrapperLoading'
                  : 'rightCornerButtonWrapper',
              )}
            >
              <Button
                className="rightCornerButton"
                primaryTransparent
                aria-label={rightCornerAriaLabel}
                icon={rightCornerIcon}
                onClick={onRightCornerBtnClick}
              />
            </div>
          )}
        </div>

        {subInfo && <span className="legend">{subInfo}</span>}
        {btnLabel && onBtnClick && (
          <div className="statsCardActions">
            <Button
              disabled={comingSoon}
              primaryTransparent
              aria-label={btnAriaLabel}
              onClick={onBtnClick}
            >
              {btnLabel}
            </Button>
            {comingSoon && (
              <span className="comingSoonLabel">
                {t('coming_soon_label', { defaultValue: 'In Kürze verfügbar' })}
              </span>
            )}
            {secondaryBtnLabel && onSecondaryBtnClick && (
              <Button
                primaryTransparent
                aria-label={secondaryBtnAriaLabel}
                onClick={onSecondaryBtnClick}
              >
                {secondaryBtnLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ClassbookCourseStats: React.FC<
  ClassbookCourseStatsProps
> = props => {
  const { t } = useTranslation();
  return (
    <div className="currentTeachingUnitStats">
      <StatsCard
        value={props.statsStudentsCount}
        label={t('classbook:coursestats:students', {
          defaultValue: 'Schüler:innen',
        })}
        btnLabel={t('classbook:coursestats:button_seatingplan', {
          defaultValue: 'Sitzplan anzeigen',
        })}
        btnAriaLabel={t('classbook:coursestats:button_seatingplan_label', {
          defaultValue: 'Sitzplan anzeigen: Öffnet Unterseite Sitzplan',
        })}
        onBtnClick={props.onBtnShowSeatingPlanClick}
        secondaryBtnLabel={t(
          'classbook:coursestats:button_student_randomizer',
          { defaultValue: 'Schüler:in auslosen' },
        )}
        secondaryBtnAriaLabel={t(
          'classbook:coursestats:button_student_randomizer_label',
          { defaultValue: 'Öffnet Dialog zum Auslosen eines Schülers' },
        )}
        onSecondaryBtnClick={props.onStudentRandomizerClick}
      />
      <StatsCard
        value={props.statsAbsentStudentsCount}
        label={t('classbook:coursestats:absence', { defaultValue: 'Abwesend' })}
        btnLabel={t('classbook:coursestats:button_absences', {
          defaultValue: 'Absenzen anzeigen',
        })}
        btnAriaLabel={t('classbook:coursestats:button_absences_label', {
          defaultValue:
            'Absenzen anzeigen: Öffnet Dialog zum Prüfen der Absenzen',
        })}
        rightCornerIcon={<ArrowSyncRegular />}
        rightCornerLoading={props.isAbsencesLoading}
        onRightCornerBtnClick={props.onReloadAbsencesClick}
        rightCornerAriaLabel={t(
          'classbook:coursestats:button_sync_aria_label',
          { defaultValue: 'Synchronisieren der Absenzen' },
        )}
        onBtnClick={props.onBtnShowAbsencesClick}
      />
      <StatsCard
        value={props.statsOpenHomeworkCount}
        label={t('classbook:coursestats:assignments_stats', {
          defaultValue: 'Aufgaben: {{count}}',

          count: props.statsOpenHomeworkCount,
        })}
        btnLabel={t('classbook:coursestats:button_assignments', {
          defaultValue: 'Aufgaben anzeigen',
        })}
        btnAriaLabel={t('classbook:coursestats:button_assignments_label', {
          defaultValue:
            'Aufgaben anzeigen: Öffnet Dialog zum Anzeigen der Aufgaben',
        })}
        onBtnClick={props.onBtnShowHomeworkClick}
      />

      <StatsCard
        value={props.statsClassServicesCount}
        label={t('classbook:coursestats:service', {
          defaultValue: 'Dienst',
          count: props.statsClassServicesCount,
        })}
        btnLabel={t('classbook:coursestats:button_services', {
          defaultValue: 'Dienste anzeigen',
        })}
        btnAriaLabel={t('classbook:coursestats:button_services_label', {
          defaultValue:
            'Dienste anzeigen: Öffnet Dialog zum Anzeigen der Klassendienste',
        })}
        onBtnClick={props.onBtnShowClassServicesClick}
      />
    </div>
  );
};
